import React, { useEffect } from "react";
import LoadingBlock from "components/Loading/LoadingBlock";
import { parseIncludesForItem } from "@tiicker/util/lib/contentful/helpers";
import {
  BrandDetailDocument,
  ContentfulContentType,
  entryAs,
  InsightDocument,
  NonBrandAuthor,
  PageSection,
  PageSectionKey,
} from "@tiicker/util/lib/contentful/types";
import { useZaiusEvents, ZaiusEventType } from "source/zaius/helper";
import { InsightsSidebar } from "components/Sidebar/InsightsSidebar/InsightsSidebar";
import { useRouter } from "next/router";
import { GetStaticProps, GetStaticPaths } from "next";
import { getContentfulClient } from "@tiicker/util/lib/contentful/client";
import { EntryCollection, Asset } from "contentful";
import ArticleMainContent from "components/Articles/ArticleMainContent/ArticleMainContent";
import ArticleRichText from "components/Articles/ArticleRichText/ArticleRichText";
import getBaseUrl from "@tiicker/util/lib/get-base-url";
import { useRecommendations } from "components/RecommendationContext/RecommendationContext";
import { EntityType } from "$gql/types.gen";
import SimpleLayout from "../../components/SimpleLayout/SimpleLayout";
import * as DateIso from "@tiicker/util/lib/date-iso";
import InsightsPerkCarousel from "components/Insights/PerkCarousel/InsightsPerkCarousel";
import StartEarningPerksMadi from "components/StartEarningPerksMadi/StartEarningPerksMadi";
import Four04 from "components/404/404";
import NavbarLayout from "components/Layout/NavbarLayout";

type Props = {
  slug: string | null;
  article: EntryCollection<InsightDocument> | null;
  joinNowCTA: EntryCollection<PageSection>;
};

const InsightV2 = ({ slug, article, joinNowCTA }: Props) => {
  const router = useRouter();
  const { isFallback } = router;

  const recommendations = useRecommendations();

  useEffect(() => {
    if (slug && article) {
      recommendations.addView(slug, EntityType.Article);
    }
  }, [slug, article]);

  useZaiusEvents(
    [
      {
        type: ZaiusEventType.ProductEvent,
        productId: article?.items[0]?.sys?.id || "",
      },
    ],
    () => article !== null
  );

  if (article === null) {
    return (
      <NavbarLayout>
        <Four04 />
      </NavbarLayout>
    );
  }

  if (isFallback) {
    return <LoadingBlock />;
  }

  const fields = article.items[0].fields;

  const author = fields.author
    ? parseIncludesForItem<BrandDetailDocument | NonBrandAuthor>(
        article.includes,
        fields.author.sys.id
      )
    : null;

  if (!author) {
    console.error(`Unable to find author for article: ${fields.slug}`);
    return (
      <NavbarLayout>
        <Four04 />
      </NavbarLayout>
    );
  }

  const brandDetailAuthor = entryAs<BrandDetailDocument>(
    author,
    ContentfulContentType.BrandDetail
  );
  const nonBrandAuthor = entryAs<NonBrandAuthor>(
    author,
    ContentfulContentType.NonBrandAuthor
  );

  const associatedBrand =
    fields.associatedBrands && fields.associatedBrands.length > 0
      ? parseIncludesForItem<BrandDetailDocument>(
          article.includes,
          fields.associatedBrands[0].sys.id
        )
      : null;

  // 1) Use author's ticker symbol if authored by a brand
  // 2) Use primary associated brand's ticker symbol is author is not a brand
  // 3) Use first related ticker symbol if no primary associated brand
  // 4) Null if no brand is associated with this insight in anyway
  const brandTickerSymbol =
    entryAs<BrandDetailDocument>(author, ContentfulContentType.BrandDetail)
      ?.fields.tickerSymbol ||
    associatedBrand?.fields.tickerSymbol ||
    (fields.relatedStockTickerSymbols &&
    fields.relatedStockTickerSymbols.length > 0
      ? fields.relatedStockTickerSymbols[0]
      : "");

  const asset = parseIncludesForItem<Asset>(
    article.includes,
    fields.heroImage?.sys.id
  ) as Asset | null;
  const fileUrl = asset ? asset.fields.file.url.replace("//", "https://") : "";

  const fullUrl = `${getBaseUrl()}insights/${fields.slug}`;

  return (
    <SimpleLayout
      title={fields.title}
      description={fields.description}
      url={fullUrl}
      imageUrl={fileUrl}
      badge={fields.category}
      date={DateIso.toIsoDate(fields.publishedDate)}
      symbols={fields.relatedStockTickerSymbols || []}
      slug={fields.slug}
      author={
        brandDetailAuthor?.fields.brandName || nonBrandAuthor?.fields.name
      }
      beforeFooter={
        joinNowCTA &&
        joinNowCTA.items &&
        joinNowCTA.items.length > 0 && (
          <StartEarningPerksMadi page={joinNowCTA.items[0]} />
        )
      }
    >
      <ArticleMainContent
        title={fields.title}
        slug={fields.slug}
        sidebarContent={
          <InsightsSidebar
            tickerSymbol={brandTickerSymbol}
            excludeInsightId={article.items[0].sys.id}
            relatedInsightIds={(
              article.items[0].fields.relatedArticles || []
            )?.map((a) => a.sys.id)}
            authorName={nonBrandAuthor?.fields.name}
          />
        }
      >
        <ArticleRichText
          richText={fields.content}
          contentIncludes={article.includes}
        />
        <InsightsPerkCarousel
          tickerSymbols={fields.relatedStockTickerSymbols || []}
        />
      </ArticleMainContent>
    </SimpleLayout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const client = getContentfulClient("contentful");
  const allArticles = await client.getEntries<InsightDocument>({
    content_type: ContentfulContentType.Article,
    select: "fields.slug",
    limit: 1000,
  });

  const paths = allArticles.items.map(
    (article) => `/insights/${article.fields.slug}`
  );

  return { paths: paths, fallback: true };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const slug = params && params.slug ? params.slug : "";

  if (Array.isArray(slug) || slug === "") {
    return {
      props: {
        slug: null,
        article: null,
      },
      revalidate: 100,
    };
  }

  const client = getContentfulClient("contentful");
  const article = await client.getEntries<InsightDocument>({
    content_type: ContentfulContentType.Article,
    limit: 1,
    "fields.slug[in]": slug,
  });

  const joinNowCTA = await client.getEntries<PageSection>({
    content_type: ContentfulContentType.PageSection,
    ["fields.sectionKey"]: PageSectionKey.JoinNowCTA,
  });

  if (article.items.length === 0) {
    return {
      props: {
        slug,
        article: null,
        openGraphTwitterData: [],
      },
      revalidate: 100,
    };
  }
  const content = article.items[0];
  const asset = content.fields.heroImage
    ? (parseIncludesForItem(article.includes, content.fields.heroImage.sys.id) as unknown as Asset)
    : undefined;

  const openGraphTwitterData = [
    {
      property: "og:image",
      content: asset ? 'https:' + asset.fields.file.url : `${getBaseUrl()}images/TiickerTM-WhiteBackground.png`,
      key: "ogimage",
    },
    {
      property: "og:image:width",
      content: "800",
      key: "ogimagewidth",
    },
    {
      property: "og:image:height",
      content: "600",
      key: "ogimageheight",
    },
    {
      property: "og:url",
      content: `${getBaseUrl()}insights/${slug}`,
      key: "ogurl",
    },
    {
      property: "og:title",
      content: content.fields.title,
      key: "ogtitle",
    },
    {
      property: "og:description",
      content: content.fields.description,
      key: "ogdesc",
    },
    {
      property: "og:type",
      content: "article",
      key: "website",
    },
    {
      property: "twitter:image",
      content: asset
        ? "https:" + asset.fields.file.url
        : `${getBaseUrl()}images/TiickerTM-WhiteBackground.png`,
    },
  ];

    const title = content.fields.title
    const description =  content.fields.description
  return {
    props: {
      article,
      slug,
      joinNowCTA,
      openGraphTwitterData,
      title, 
      description
    },
    revalidate: 100,
  };
};

export default InsightV2;
